import ProjectTitle from "@/components/layout/ProjectTitle";
import { Skeleton } from "@/components/ui/skeleton";

import { convertStringToNumber } from "@/libs/utils";
import { Step1Skeleton } from "@/pages/submit/Program";
import useProgram from "@/programs/hooks/useProgram";
import { FC, lazy, Suspense, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
// Lazy loading des composants Step
const Step1 = lazy(() => import("@/programs/components/steps/Step1"));
const Step2 = lazy(() => import("@/programs/components/steps/Step2"));
const Step3 = lazy(() => import("@/programs/components/steps/Step3"));
const Step4 = lazy(() => import("@/programs/components/steps/Step4"));
const Step5 = lazy(() => import("@/programs/components/steps/Step5"));
const Step6 = lazy(() => import("@/programs/components/steps/Step6"));
const Step7 = lazy(() => import("@/programs/components/steps/Step7"));
const Step8 = lazy(() => import("@/programs/components/steps/Step8"));
const Step9 = lazy(() => import("@/programs/components/steps/Step9"));

const StepRenderer: FC<{
  currentStep: number;
  onCurrentChange: (step: number) => void;
}> = ({ currentStep, onCurrentChange }) => {
  const stepRef = useRef<HTMLDivElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const { id } = useParams<{ id: string }>();
  const idParams = convertStringToNumber(id);
  const projectId = searchParams.get("project") || "";
  const { programData } = useProgram();

  useEffect(() => {
    const stepParam = searchParams.get("step");
    const step = stepParam ? Number(stepParam) : NaN;
    const isStepNumber = !isNaN(step) && Number.isInteger(step);

    if (isStepNumber) {
      onCurrentChange(step);
      searchParams.delete("step");
      setSearchParams(searchParams); // Met à jour l'URL sans le paramètre
    }
  }, [searchParams, onCurrentChange, setSearchParams]);

  return (
    <>
      {currentStep > 1 && (
        <ProjectTitle
          project={
            programData[idParams]?.projects?.find((p) => p.id == projectId) ||
            {}
          }
        />
      )}
      <div
        ref={stepRef}
        className={currentStep != 1 ? "rounded-[20px] border px-6 pb-6" : ""}
      >
        <Suspense
          fallback={currentStep === 1 ? <Step1Skeleton /> : <Step2Skeleton />}
        >
          {currentStep === 1 && <Step1 onCurrentChange={onCurrentChange} />}
          {currentStep === 2 && <Step2 onCurrentChange={onCurrentChange} />}
          {currentStep === 3 && <Step3 onCurrentChange={onCurrentChange} />}
          {currentStep === 4 && <Step4 onCurrentChange={onCurrentChange} />}
          {currentStep === 5 && <Step5 onCurrentChange={onCurrentChange} />}
          {currentStep === 6 && <Step6 onCurrentChange={onCurrentChange} />}
          {currentStep === 7 && <Step7 onCurrentChange={onCurrentChange} />}
          {currentStep === 8 && <Step8 onCurrentChange={onCurrentChange} />}
          {currentStep === 9 && <Step9 onCurrentChange={onCurrentChange} />}
        </Suspense>
      </div>
    </>
  );
};

export default StepRenderer;

export const Step2Skeleton = () => {
  const { t } = useTranslation("submit", {
    keyPrefix: "project.detail",
  });
  return (
    <>
      <h2 className="mb-5 mt-4 text-[20px] font-bold text-[#626262]">
        {t("Détails du projet")}
      </h2>
      <p className="mb-4 text-[#595959]">
        {t(
          "Vous allez remplir un formulaire pour compléter les informations de votre projet. Le formulaire vous guidera à travers les différentes sections à remplir. Vos réponses seront automatiquement sauvegardées, vous permettant de revenir et de continuer à tout moment."
        )}
      </p>

      <div className=" flex flex-col w-full gap-4 ">
        <div>
          <p>{t("Nom du projet")} *</p>
          <Skeleton className="bg-[#ecedee] w-full h-12 rounded-sm" />
        </div>
        <div>
          <p>{t("Description du projet")}</p>
          <Skeleton className="bg-[#ecedee] w-full h-[100px] rounded-sm" />
        </div>
        <div>
          <p>{t("Pays")}*</p>
          <Skeleton className="  bg-[#ecedee] w-full h-12 rounded-sm" />
        </div>
        <div>
          <p>{t("Organisme receveur des fonds")}</p>
          <div className="grid grid-cols-2 gap-4 px-5 ">
            <Skeleton className="  bg-[#ecedee] w-full h-12 rounded-sm" />
            <Skeleton className="  bg-[#ecedee] w-full h-12 rounded-sm" />
          </div>
        </div>
        <div>
          <p>{t("Intervenants locaux financés")}</p>
          <div className="grid grid-cols-2 gap-4 px-5 ">
            <Skeleton className="  bg-[#ecedee] w-full h-12 rounded-sm" />
            <Skeleton className="  bg-[#ecedee] w-full h-12 rounded-sm" />
          </div>
        </div>
        <div>
          <p>{t("Responsable local du Projet")}*</p>
          <div className="grid grid-cols-3 gap-4 px-5 mt-5">
            <div>
              <Skeleton className="  bg-[#ecedee] w-full h-24 rounded-sm" />
            </div>
            <div className="flex flex-col gap-4">
              <Skeleton className="  bg-[#ecedee] w-full h-12 rounded-sm" />
              <Skeleton className="  bg-[#ecedee] w-full h-12 rounded-sm" />
              <Skeleton className="  bg-[#ecedee] w-full h-12 rounded-sm" />
            </div>
            <div className="flex flex-col gap-4">
              <Skeleton className="  bg-[#ecedee] w-full h-12 rounded-sm" />

              <Skeleton className="  bg-[#ecedee] w-full h-12 rounded-sm" />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const Step3Skeleton = () => {
  const { t } = useTranslation("submit", {
    keyPrefix: "project.echeance",
  });
  return (
    <>
      <div>
        <h2 className="mb-5 mt-4 text-[20px] font-bold text-[#626262]">
          {t("Échéancier du projet")}
        </h2>
        <p className="mb-4 text-[#595959]">
          {t(
            "Indiquez la date de lancement du projet, qui peut être dans le passé si le projet a déjà commencé, ainsi que la durée prévue en mois."
          )}
        </p>
        <div className="grid grid-cols-2 gap-4 px-5 ">
          <div>
            <p>{t("Date de lancement")} *</p>
            <Skeleton className="  bg-[#ecedee] w-full h-10 rounded-sm" />
          </div>
          <div>
            <p>{t("Durée prévue (mois)")} *</p>
            <Skeleton className="  bg-[#ecedee] w-full h-10 rounded-sm" />
          </div>
        </div>
      </div>
      <div>
        <h2 className="mb-5 mt-4 text-[20px] font-bold text-[#626262]">
          {t("Objectifs du projet")}
        </h2>
        <p className="mb-4 text-[#595959]">
          {t(
            "Définissez les buts principaux du projet, en précisant les résultats attendus et les impacts recherchés. Ces objectifs guideront toutes les étapes de sa réalisation."
          )}
        </p>
        <div className=" flex flex-col w-full gap-4 ">
          <div>
            <p>{t("Objectifs à atteindre")}</p>
            <Skeleton className="bg-[#ecedee] w-full h-[100px] rounded-sm" />
          </div>
          <div>
            <p>{t("Actions à mettre en oeuvre")}</p>
            <Skeleton className="bg-[#ecedee] w-full h-[100px] rounded-sm" />
          </div>
          <div>
            <p>{t("Bénéfices écologiques ou sociaux attendus")}</p>
            <Skeleton className="bg-[#ecedee] w-full h-[100px] rounded-sm" />
          </div>
          <div>
            <p>{t("Critères d'atteinte des objectifs du projet")}</p>
            <Skeleton className="bg-[#ecedee] w-full h-[100px] rounded-sm" />
          </div>
        </div>
      </div>
    </>
  );
};
